/* eslint-disable */
import React from 'react';
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import { ExpandMore, HelpOutline } from '@mui/icons-material';
import type { FilterOptions } from '../../hooks/useRelease';

interface FiltersProps {
  filters: FilterOptions;
  filterOptions: {
    statuses: string[];
    types: string[];
    assignees: string[];
    productScopes: string[];
    swTeams: string[];
  };
  onFiltersChange: (filters: FilterOptions) => void;
}

export const Filters: React.FC<FiltersProps> = ({
  filters,
  filterOptions,
  onFiltersChange,
}) => {
  const handleFilterChange = (
    filterKey: keyof FilterOptions,
    value: string | boolean,
    isArray: boolean = false
  ) => {
    if (isArray) {
      const currentArray = (filters[filterKey] as string[]) || [];
      const newArray = currentArray.includes(value as string)
        ? currentArray.filter(v => v !== value)
        : [...currentArray, value as string];
      onFiltersChange({ ...filters, [filterKey]: newArray.length > 0 ? newArray : undefined });
    } else {
      onFiltersChange({ ...filters, [filterKey]: value });
    }
  };

  const clearFilter = (filterKey: keyof FilterOptions) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [filterKey]: _, ...rest } = filters;
    onFiltersChange(rest);
  };

  const activeFiltersCount = Object.keys(filters).filter(
    key => {
      const value = filters[key as keyof FilterOptions];
      if (Array.isArray(value)) return value.length > 0;
      return value !== undefined;
    }
  ).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Filters
          </Typography>
          {activeFiltersCount > 0 && (
            <Chip label={activeFiltersCount} size="small" color="primary" />
          )}
          <Tooltip title="Filter tickets by status, type, assignee, product scope, SW Team, validation issues, or late status">
            <HelpOutline fontSize="small" color="action" sx={{ cursor: 'help' }} />
          </Tooltip>
        </Box>
      </Box>

      {/* Status Filter */}
      {filterOptions.statuses.length > 0 && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle2">Status</Typography>
            {filters.status && filters.status.length > 0 && (
              <Chip
                label={filters.status.length}
                size="small"
                sx={{ ml: 1 }}
                onDelete={() => clearFilter('status')}
              />
            )}
          </AccordionSummary>
          <AccordionDetails>
            <FormGroup>
              {filterOptions.statuses.map(status => (
                <FormControlLabel
                  key={status}
                  control={
                    <Checkbox
                      checked={filters.status?.includes(status) || false}
                      onChange={() => handleFilterChange('status', status, true)}
                      size="small"
                    />
                  }
                  label={status}
                />
              ))}
            </FormGroup>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Type Filter */}
      {filterOptions.types.length > 0 && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle2">Type</Typography>
            {filters.type && filters.type.length > 0 && (
              <Chip
                label={filters.type.length}
                size="small"
                sx={{ ml: 1 }}
                onDelete={() => clearFilter('type')}
              />
            )}
          </AccordionSummary>
          <AccordionDetails>
            <FormGroup>
              {filterOptions.types.map(type => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={filters.type?.includes(type) || false}
                      onChange={() => handleFilterChange('type', type, true)}
                      size="small"
                    />
                  }
                  label={type}
                />
              ))}
            </FormGroup>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Assignee Filter */}
      {filterOptions.assignees.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle2">Assignee</Typography>
            {filters.assignee && filters.assignee.length > 0 && (
              <Chip
                label={filters.assignee.length}
                size="small"
                sx={{ ml: 1 }}
                onDelete={() => clearFilter('assignee')}
              />
            )}
          </AccordionSummary>
          <AccordionDetails>
            <FormGroup>
              {filterOptions.assignees.map(assignee => (
                <FormControlLabel
                  key={assignee}
                  control={
                    <Checkbox
                      checked={filters.assignee?.includes(assignee) || false}
                      onChange={() => handleFilterChange('assignee', assignee, true)}
                      size="small"
                    />
                  }
                  label={assignee}
                />
              ))}
            </FormGroup>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Product Scope Filter */}
      {filterOptions.productScopes.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle2">Product X Tech Scope</Typography>
            {filters.productScope && filters.productScope.length > 0 && (
              <Chip
                label={filters.productScope.length}
                size="small"
                sx={{ ml: 1 }}
                onDelete={() => clearFilter('productScope')}
              />
            )}
          </AccordionSummary>
          <AccordionDetails>
            <FormGroup>
              {filterOptions.productScopes.map(scope => (
                <FormControlLabel
                  key={scope}
                  control={
                    <Checkbox
                      checked={filters.productScope?.includes(scope) || false}
                      onChange={() => handleFilterChange('productScope', scope, true)}
                      size="small"
                    />
                  }
                  label={scope}
                />
              ))}
            </FormGroup>
          </AccordionDetails>
        </Accordion>
      )}

      {/* SW Team Filter */}
      {filterOptions.swTeams.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle2">SW Team</Typography>
            {filters.swTeam && filters.swTeam.length > 0 && (
              <Chip
                label={filters.swTeam.length}
                size="small"
                sx={{ ml: 1 }}
                onDelete={() => clearFilter('swTeam')}
              />
            )}
          </AccordionSummary>
          <AccordionDetails>
            <FormGroup>
              {filterOptions.swTeams.map(team => (
                <FormControlLabel
                  key={team}
                  control={
                    <Checkbox
                      checked={filters.swTeam?.includes(team) || false}
                      onChange={() => handleFilterChange('swTeam', team, true)}
                      size="small"
                    />
                  }
                  label={team}
                />
              ))}
            </FormGroup>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Validation Issues Filter */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Validation Issues</Typography>
          {filters.hasValidationIssues !== undefined && (
            <Chip
              label={filters.hasValidationIssues ? 'Has Issues' : 'No Issues'}
              size="small"
              sx={{ ml: 1 }}
              onDelete={() => clearFilter('hasValidationIssues')}
            />
          )}
        </AccordionSummary>
        <AccordionDetails>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.hasValidationIssues === true}
                  onChange={(e) => handleFilterChange('hasValidationIssues', e.target.checked)}
                  size="small"
                />
              }
              label="Has validation issues"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.hasValidationIssues === false}
                  onChange={(e) => handleFilterChange('hasValidationIssues', !e.target.checked)}
                  size="small"
                />
              }
              label="No validation issues"
            />
          </FormGroup>
        </AccordionDetails>
      </Accordion>

      {/* Late Tickets Filter */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Late Tickets</Typography>
          {filters.isLate !== undefined && (
            <Chip
              label={filters.isLate ? 'Late' : 'On Time'}
              size="small"
              sx={{ ml: 1 }}
              onDelete={() => clearFilter('isLate')}
            />
          )}
        </AccordionSummary>
        <AccordionDetails>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.isLate === true}
                  onChange={(e) => handleFilterChange('isLate', e.target.checked)}
                  size="small"
                />
              }
              label="Late tickets"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.isLate === false}
                  onChange={(e) => handleFilterChange('isLate', !e.target.checked)}
                  size="small"
                />
              }
              label="On time tickets"
            />
          </FormGroup>
        </AccordionDetails>
      </Accordion>

      {activeFiltersCount > 0 && (
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography
            variant="body2"
            component="button"
            onClick={() => onFiltersChange({})}
            sx={{
              color: 'primary.main',
              cursor: 'pointer',
              textDecoration: 'underline',
              border: 'none',
              background: 'none',
              '&:hover': {
                color: 'primary.dark',
              },
            }}
          >
            Clear all filters
          </Typography>
        </Box>
      )}
    </Box>
  );
};
